/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { NextRequest } from "next/server";

import { getLocalePrefixMode } from "../utils/locale-path.utils";

import { getBestLocaleFromRequest } from "./locale";

const EULA_REFERENCE_PATH = "eula-reference";
const LOCALE_SUFFIX_REGEX = /^-([a-zA-Z]{2}(-[a-zA-Z]{2})?)$/;

/**
 * Normalizes locale casing so the region subtag is uppercase.
 *
 * @param locale - Raw locale string (e.g., `"en-us"`, `"ja"`).
 * @returns Normalized locale (e.g., `"en-US"`, `"ja"`).
 */
const normalizeLocale = (locale: string): string => {
	const dashIndex = locale.indexOf("-");
	if (dashIndex === -1) return locale;
	return locale.slice(0, dashIndex + 1) + locale.slice(dashIndex + 1).toUpperCase();
};

/**
 * Splits a compound eula slug into its first URL segment and the remainder.
 *
 * @param eulaSlug - Eula page path, simple or compound (e.g., `"eula"`, `"eula/content"`).
 * @returns `basePath` — first segment matched in the URL; `subPath` — everything after the first `/`, or `""`.
 *
 * @example
 * parseEulaSlug("eula")          // { basePath: "eula", subPath: "" }
 * parseEulaSlug("eula/content")  // { basePath: "eula", subPath: "content" }
 * parseEulaSlug("eula/foo/bar")  // { basePath: "eula", subPath: "foo/bar" }
 */
const parseEulaSlug = (eulaSlug: string): { basePath: string; subPath: string } => {
	const slashIndex = eulaSlug.indexOf("/");
	if (slashIndex === -1) return { basePath: eulaSlug, subPath: "" };
	return {
		basePath: eulaSlug.slice(0, slashIndex),
		subPath: eulaSlug.slice(slashIndex + 1),
	};
};

/**
 * Issues a 302 redirect to `pathname`, preserving the original query string and host.
 *
 * @param request - The incoming Next.js request whose `nextUrl` is mutated in-place.
 * @param pathname - Absolute pathname for the redirect target (e.g., `"/en-US/eula"`).
 * @returns A 302 `Response` with the `Location` header set.
 */
const redirect = (request: NextRequest, pathname: string): Response => {
	const url = request.nextUrl.clone();
	url.pathname = pathname;
	return Response.redirect(url);
};

/**
 * Returns the `lang` query param when it is a recognised locale, otherwise `""`.
 *
 * Prevents arbitrary strings from being used as redirect targets while still
 * honouring explicit per-request locale overrides.
 *
 * @param request - Incoming request to read search params from.
 * @param locales - Allowlist of supported locale codes.
 * @returns The validated locale string, or `""` if absent or unrecognised.
 */
const getValidatedLang = (request: NextRequest, locales: string[]): string => {
	const lang = new URLSearchParams(request.nextUrl.search).get("lang") ?? "";
	return lang && locales.includes(lang) ? lang : "";
};

/**
 * Builds a canonical `eula-reference` pathname for the given locale.
 *
 * @param locale - Target locale (e.g., `"es-ES"`, `"ja"`). Region subtag is normalised to uppercase.
 * @param eulaReferenceSlug - Product-specific slug embedded in the path (e.g., `"twinmotion"`, `"content"`).
 * @param withLocalePrefix - `true` in `always`/`as-needed` modes; `false` in `never` mode.
 * @returns e.g. `/es-ES/eula-reference/twinmotion-es-es` or `/eula-reference/twinmotion-es-es`.
 */
const buildEulaRefPath = (locale: string, eulaReferenceSlug: string, withLocalePrefix: boolean): string => {
	const lc = locale.toLowerCase();
	const refPath = `/${EULA_REFERENCE_PATH}/${eulaReferenceSlug}-${lc}`;
	return withLocalePrefix ? `/${normalizeLocale(locale)}${refPath}` : refPath;
};

/**
 * Returns `true` when the path segments following the eula base slug are a valid
 * match for the expected eula page (including sub-paths and extra trailing segments).
 *
 * Matches when:
 * - `restSegments` is empty — the URL ends exactly at the eula base (e.g., `/ja/eula`).
 * - First segment equals `eulaReferenceSlug` — legacy redirect-target form.
 * - Joined rest equals `eulaSubPath` or starts with `eulaSubPath/` — handles compound slugs
 *   like `eula/content` and tolerates extra trailing segments (e.g., `/ja/eula/content/extra`).
 *
 * @param restSegments - URL segments after the eula base path segment.
 * @param eulaReferenceSlug - Product identifier (e.g., `"content"`, `"twinmotion"`).
 * @param eulaSubPath - Sub-path portion of the eula slug (e.g., `"content"` from `"eula/content"`), or `""`.
 * @returns `true` if the path should trigger a redirect, `false` to pass through.
 */
const eulaPathMatches = (restSegments: string[], eulaReferenceSlug: string, eulaSubPath: string): boolean => {
	const first = restSegments[0];
	if (!first) return true;
	if (first === eulaReferenceSlug) return true;
	if (!eulaSubPath) return false;
	const rest = restSegments.join("/");
	return rest === eulaSubPath || rest.startsWith(`${eulaSubPath}/`);
};

/**
 * Shared redirect resolver for `eula-reference` paths, used by all three locale prefix modes.
 *
 * Applies the following priority order:
 * 1. URL locale or `lang` param is the default locale → redirect to `defaultEulaPath`.
 * 2. `lang` param differs from the URL locale → redirect to the `lang` locale's eula-reference.
 * 3. URL locale already matches the slug's locale suffix → no redirect needed.
 * 4. Locale suffix is missing or wrong → redirect to eula-reference with the correct suffix.
 *
 * Returns `void` (no redirect) when the path does not start with `eulaReferenceSlug` or
 * has an unrecognised suffix pattern.
 *
 * @param request - Incoming Next.js request.
 * @param locale - Locale extracted from the URL (or inferred from the request in `never` mode).
 * @param defaultLocale - Site default locale (e.g., `"en-US"`).
 * @param eulaReferenceSlug - Product identifier in the eula-reference path (e.g., `"twinmotion"`).
 * @param restPath - Segments after `eula-reference/` joined with `/` (e.g., `"twinmotion-ja"`).
 * @param validatedLang - Validated `lang` query param, or `""` if absent/invalid. Pass `""` in `never` mode as the locale already incorporates it.
 * @param defaultEulaPath - Canonical pathname for the default-locale eula page (e.g., `"/en-US/eula"` or `"/eula"`).
 * @param withLocalePrefix - Whether redirect targets should include a locale prefix.
 * @returns A redirect `Response`, or `void` to continue without redirecting.
 */
function resolveEulaRefRedirect(
	request: NextRequest,
	locale: string,
	defaultLocale: string,
	eulaReferenceSlug: string,
	restPath: string,
	validatedLang: string,
	defaultEulaPath: string,
	withLocalePrefix: boolean,
): Response | void {
	if (!restPath.startsWith(eulaReferenceSlug)) return;

	const suffix = restPath.slice(eulaReferenceSlug.length);
	if (suffix !== "" && !LOCALE_SUFFIX_REGEX.test(suffix)) {
		return;
	}

	const refLocale = suffix ? suffix.slice(1) : null;

	if (locale === defaultLocale || validatedLang === defaultLocale) {
		return redirect(request, defaultEulaPath);
	}

	if (validatedLang && locale !== validatedLang) {
		return redirect(request, buildEulaRefPath(validatedLang, eulaReferenceSlug, withLocalePrefix));
	}

	if (refLocale && locale.toLowerCase() === refLocale.toLowerCase()) {
		return;
	}

	return redirect(request, buildEulaRefPath(locale, eulaReferenceSlug, withLocalePrefix));
}

/**
 * Middleware that enforces canonical EULA URL routing.
 *
 * Bidirectional redirect between the eula page and its localised eula-reference page:
 * - `/{locale}/eula` → `/{locale}/eula-reference/{eulaReferenceSlug}-{locale}` for non-default locales.
 * - `/{locale}/eula-reference/{eulaReferenceSlug}` → `/{locale}/eula` for the default locale.
 *
 * Dispatches to the appropriate handler based on the locale prefix mode read from
 * `NEXT_PUBLIC_LOCALE_MODE` (`"always"` | `"as-needed"` | `"never"`). Defaults to `"always"`.
 *
 * @param request - Incoming Next.js edge request.
 * @param locales - Full list of supported locale codes (e.g., `["en-US", "de", "ja"]`).
 * @param defaultLocale - Locale treated as the site default (e.g., `"en-US"`).
 * @param eulaReferenceSlug - Product identifier embedded in the eula-reference URL (e.g., `"twinmotion"`).
 * @param eulaSlug - Pathname for the eula page; supports sub-paths (e.g., `"eula/content"`). Defaults to `"eula"`.
 * @returns A redirect `Response` when the URL needs correction, or `void` to continue.
 */
export function processEulaReference(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	eulaReferenceSlug: string,
	eulaSlug: string = "eula",
	unsupportedLocaleMapping?: Record<string, string>,
): Response | void {
	const localePrefix = getLocalePrefixMode();
	if (localePrefix === "as-needed") {
		return processEulaReferenceAsNeeded(request, locales, defaultLocale, eulaReferenceSlug, eulaSlug);
	}
	if (localePrefix === "never") {
		return processEulaReferenceNever(
			request,
			locales,
			defaultLocale,
			eulaReferenceSlug,
			eulaSlug,
			unsupportedLocaleMapping,
		);
	}
	return processEulaReferenceAlways(request, locales, defaultLocale, eulaReferenceSlug, eulaSlug);
}

/**
 * EULA redirect handler for `always` locale prefix mode.
 *
 * Every URL is expected to carry a locale prefix (e.g., `/en-US/eula`).
 * Paths without a prefix are ignored — `processLocale` will prepend one first.
 * Unlike `as-needed`, prefix-less paths are never treated as the default locale.
 * Redirect targets always include the locale prefix.
 *
 * @see processEulaReference for parameter descriptions.
 */
function processEulaReferenceAlways(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	eulaReferenceSlug: string,
	eulaSlug: string,
): Response | void {
	const { pathname } = request.nextUrl;

	// Early exit: check if path contains locale prefix
	const pathnameHasLocale = locales.some((loc) => pathname.startsWith(`/${loc}/`) || pathname === `/${loc}`);
	if (!pathnameHasLocale) return;

	// Parse path: " / es-ES / eula-reference / realityscan-es-es "
	const pathSegments = pathname.split("/");
	const locale = pathSegments[1];
	const slug = pathSegments[2];

	if (!slug) return;

	const { basePath: eulaBasePath, subPath: eulaSubPath } = parseEulaSlug(eulaSlug);

	// Early exit: not an eula or eula-reference path
	const isEulaPath = slug === eulaBasePath;
	const isEulaRefPath = slug === EULA_REFERENCE_PATH;
	if (!isEulaPath && !isEulaRefPath) return;

	// Validate lang parameter once
	const validatedLang = getValidatedLang(request, locales);
	const rest = pathSegments.slice(3); // segments after /{locale}/{slug}/

	if (isEulaRefPath) {
		return resolveEulaRefRedirect(
			request,
			locale,
			defaultLocale,
			eulaReferenceSlug,
			rest.join("/"),
			validatedLang,
			`/${defaultLocale}/${eulaSlug}`, // always mode: default eula canonical path keeps locale prefix
			true, // always mode: redirect targets include locale prefix
		);
	}

	// Sub-path doesn't match this eula slug (e.g. /ja/eula/other when eulaSlug='eula/content') — pass through
	if (!eulaPathMatches(rest, eulaReferenceSlug, eulaSubPath)) {
		return;
	}

	const effectiveLocale = validatedLang || locale; // lang param overrides URL locale
	// Default locale eula page serves directly — no redirect to eula-reference
	if (effectiveLocale === defaultLocale) {
		return;
	}

	return redirect(request, buildEulaRefPath(effectiveLocale, eulaReferenceSlug, true));
}

/**
 * EULA redirect handler for `as-needed` locale prefix mode.
 *
 * The default locale has no URL prefix (e.g., `/eula`); non-default locales do (e.g., `/de/eula`).
 * Unlike `always`, prefix-less paths are valid and treated as the default locale.
 * Unlike `never`, locale is derived from the URL segment, not request headers/cookies.
 * Explicit default-locale prefixes (e.g., `/en-US/eula`) are redirected to the prefix-less canonical form.
 * Redirect targets for non-default locales still include the locale prefix.
 *
 * @see processEulaReference for parameter descriptions.
 */
function processEulaReferenceAsNeeded(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	eulaReferenceSlug: string,
	eulaSlug: string,
): Response | void {
	const { pathname } = request.nextUrl;
	const pathnameHasLocale = locales.some((loc) => pathname.startsWith(`/${loc}/`) || pathname === `/${loc}`);

	const segments = pathname.split("/");
	const locale = pathnameHasLocale ? segments[1] : defaultLocale; // prefix-less path = default locale in as-needed mode
	const slugIndex = pathnameHasLocale ? 2 : 1; // slug is at [2] with locale prefix, [1] without
	const slug = segments[slugIndex];
	if (!slug) return;

	const { basePath: eulaBasePath, subPath: eulaSubPath } = parseEulaSlug(eulaSlug);

	// Early exit: not an eula or eula-reference path
	const isEulaPath = slug === eulaBasePath;
	const isEulaRefPath = slug === EULA_REFERENCE_PATH;
	if (!isEulaPath && !isEulaRefPath) return;

	// Validate lang parameter once
	const validatedLang = getValidatedLang(request, locales);
	const rest = segments.slice(slugIndex + 1); // segments after /{locale?}/{slug}/

	if (isEulaRefPath) {
		return resolveEulaRefRedirect(
			request,
			locale,
			defaultLocale,
			eulaReferenceSlug,
			rest.join("/"),
			validatedLang,
			`/${eulaSlug}`, // as-needed mode: default eula canonical path has no locale prefix
			true, // non-default locale redirect targets include locale prefix
		);
	}

	// Sub-path doesn't match this eula slug — pass through
	if (!eulaPathMatches(rest, eulaReferenceSlug, eulaSubPath)) {
		return;
	}

	const effectiveLocale = validatedLang || locale; // lang param overrides URL locale
	// Default locale (URL or lang override): explicit prefix → strip to canonical (/{eulaSlug}); no prefix → pass through
	if (effectiveLocale === defaultLocale) {
		return pathnameHasLocale ? redirect(request, `/${eulaSlug}`) : undefined;
	}

	return redirect(request, buildEulaRefPath(effectiveLocale, eulaReferenceSlug, true));
}

/**
 * EULA redirect handler for `never` locale prefix mode.
 *
 * No locale prefixes appear in URLs; the locale is inferred from the `lang` query param,
 * the `EPIC_LOCALE` cookie, or the `Accept-Language` header (via {@link getBestLocaleFromRequest}).
 * Unlike `always`/`as-needed`, redirect targets omit the locale prefix.
 * Because locale detection is done up-front, there is no separate `validatedLang` step —
 * the resolved locale already incorporates the `lang` param.
 * Paths that still carry a locale prefix are ignored — `processLocale` will strip them first.
 *
 * @see processEulaReference for parameter descriptions.
 */
function processEulaReferenceNever(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	eulaReferenceSlug: string,
	eulaSlug: string,
	unsupportedLocaleMapping?: Record<string, string>,
): Response | void {
	const { pathname } = request.nextUrl;
	const pathnameHasLocale = locales.some((loc) => pathname.startsWith(`/${loc}/`) || pathname === `/${loc}`);
	if (pathnameHasLocale) return; // processLocale will strip the prefix first; ignore until path is clean

	const segments = pathname.split("/");
	const slug = segments[1];
	if (!slug) return;

	const { basePath: eulaBasePath, subPath: eulaSubPath } = parseEulaSlug(eulaSlug);

	// Early exit: not an eula or eula-reference path
	const isEulaPath = slug === eulaBasePath;
	const isEulaRefPath = slug === EULA_REFERENCE_PATH;
	if (!isEulaPath && !isEulaRefPath) return;

	const locale = getBestLocaleFromRequest(request, locales, defaultLocale, unsupportedLocaleMapping);
	const rest = segments.slice(2); // no locale segment — slug is at [1], rest starts at [2]

	if (isEulaRefPath) {
		return resolveEulaRefRedirect(
			request,
			locale,
			defaultLocale,
			eulaReferenceSlug,
			rest.join("/"),
			"", // lang param already incorporated into locale by getBestLocaleFromRequest
			`/${eulaSlug}`,
			false, // never mode: redirect targets omit locale prefix
		);
	}

	// Default locale eula page serves directly — no redirect to eula-reference
	if (locale === defaultLocale) {
		return;
	}

	// Sub-path doesn't match this eula slug — pass through
	if (!eulaPathMatches(rest, eulaReferenceSlug, eulaSubPath)) {
		return;
	}

	return redirect(request, buildEulaRefPath(locale, eulaReferenceSlug, false));
}
